import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { useLanguage } from "@/context/LanguageContext";
import { useContent } from "@/context/ContentContext";
import { HERO_IMAGE, CTA_IMAGE, GALLERY_SEED } from "@/lib/images";
import { api } from "@/lib/api";
import useSeo from "@/hooks/useSeo";
import Reveal from "@/components/Reveal";
import SectionHeading from "@/components/SectionHeading";
import PackagesGrid from "@/components/PackagesGrid";
import ServicesPreview from "@/components/ServicesPreview";
import SectionOrnament from "@/components/SectionOrnament";
import { StarRatingDisplay } from "@/components/StarRating";
import { ChevronDown, ArrowRight, Instagram } from "lucide-react";

export default function Home() {
  const { t, lang } = useLanguage();
  const { content } = useContent();
  const igLink = content?.contact?.instagram || "https://www.instagram.com/our_promise_events";
  const igHandle = content?.contact?.instagram_handle || "@our_promise_events";
  const [reviews, setReviews] = useState([]);

  useSeo(
    "Hochzeits- & Eventplanung in der Schweiz",
    "Organisation de mariages & d'événements en Suisse",
    "Our Promise Events – persönliche Planung, Organisation und Koordination von Hochzeiten und Events in der Schweiz.",
    "Our Promise Events – planification, organisation et coordination personnalisées de mariages et d'événements en Suisse."
  );

  useEffect(() => {
    api.get("/reviews").then((r) => setReviews(r.data.slice(0, 3))).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      {/* Hero */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url('${HERO_IMAGE}')` }} />
        <div className="absolute inset-0 bg-ink/45" />
        <div className="relative z-10 max-w-4xl mx-auto px-6 text-center text-cream">
          <img src="/fotos/logo.png?v=3" alt="Our Promise Events" className="w-60 sm:w-72 lg:w-80 mx-auto mb-8 animate-fade-up drop-shadow-[0_6px_28px_rgba(0,0,0,0.45)]" style={{ animationDelay: "100ms" }} />
          <h1 data-testid="hero-title" className="font-heading font-light text-5xl sm:text-6xl lg:text-7xl leading-tight mb-6 animate-fade-up" style={{ animationDelay: "250ms" }}>
            {t("hero.claim").split("\n").map((line, i) => (
              <span key={i} className="block italic">{line}</span>
            ))}
          </h1>
          <p className="max-w-xl mx-auto text-base sm:text-lg font-light text-cream/90 mb-12 animate-fade-up" style={{ animationDelay: "400ms" }}>
            {t("hero.subtitle")}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center animate-fade-up" style={{ animationDelay: "600ms" }}>
            <Link to="/packages" data-testid="hero-cta-packages" className="bg-cream text-ink px-9 py-4 text-[11px] tracking-[0.3em] uppercase hover:bg-gold transition-colors duration-500">
              {t("hero.cta1")}
            </Link>
            <Link to="/anfrage" data-testid="hero-cta-inquiry" className="border border-cream text-cream px-9 py-4 text-[11px] tracking-[0.3em] uppercase hover:bg-cream hover:text-ink transition-colors duration-500">
              {t("hero.cta2")}
            </Link>
          </div>
        </div>
        <ChevronDown size={26} className="absolute bottom-10 left-1/2 -translate-x-1/2 text-cream/80 animate-bounce" />
      </section>

      {/* Brand message */}
      <section className="relative bg-cream py-24 sm:py-32 px-6 overflow-hidden">
        <SectionOrnament variant="dots" />
        <SectionOrnament variant="corners" />
        <SectionOrnament variant="frame" />
        <div className="relative max-w-3xl mx-auto text-center">
          <Reveal>
            <p className="text-xs tracking-[0.35em] uppercase text-gold mb-6">{t("brandMessage.overline")}</p>
            <SectionOrnament variant="divider" className="mb-8" />
            <h2 className="font-heading font-light text-3xl sm:text-4xl lg:text-5xl leading-snug mb-8">{t("brandMessage.title")}</h2>
            <p className="text-base sm:text-lg text-ink-muted leading-relaxed font-light">{t("brandMessage.body")}</p>
            <SectionOrnament variant="divider" className="mt-10" />
          </Reveal>
        </div>
      </section>

      {/* Process (Ablauf) */}
      <section className="relative bg-secondary/40 py-24 sm:py-32 px-6 lg:px-10 overflow-hidden">
        <SectionOrnament variant="dots" />
        <div className="relative max-w-[1400px] mx-auto">
          <div className="mb-10">
            <SectionHeading overline={t("process.overline")} title={t("process.title")} />
          </div>
          <SectionOrnament variant="divider" className="mb-16" />
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-10">
            {t("process.steps").map((step, i) => (
              <Reveal key={i} delay={i * 120} className="text-center">
                <div className="font-heading text-5xl text-gold mb-5">0{i + 1}</div>
                <h3 className="font-heading text-2xl mb-3 font-light">{step.t}</h3>
                <p className="text-sm text-ink-muted leading-relaxed">{step.d}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Packages */}
      <section id="packages" className="relative bg-cream py-24 sm:py-32 px-6 lg:px-10 overflow-hidden">
        <SectionOrnament variant="corners" />
        <div className="relative max-w-[1400px] mx-auto">
          <div className="mb-10">
            <SectionHeading overline={t("home.packagesOverline")} title={t("home.packagesTitle")} subtitle={t("home.packagesSub")} />
          </div>
          <SectionOrnament variant="divider" className="mb-16" />
          <PackagesGrid />
        </div>
      </section>

      {/* Services */}
      <section className="relative bg-secondary/40 py-24 sm:py-32 px-6 lg:px-10 overflow-hidden">
        <SectionOrnament variant="corners" />
        <div className="relative max-w-[1400px] mx-auto">
          <div className="mb-10">
            <SectionHeading overline={t("home.servicesOverline")} title={t("home.servicesTitle")} subtitle={t("home.servicesSub")} />
          </div>
          <SectionOrnament variant="divider" className="mb-16" />
          <ServicesPreview />
        </div>
      </section>

      {/* Impressions preview */}
      <section className="relative bg-cream py-24 sm:py-32 px-6 lg:px-10 overflow-hidden">
        <SectionOrnament variant="corners" />
        <div className="relative max-w-[1400px] mx-auto">
          <div className="mb-10">
            <SectionHeading overline={t("home.impressionsOverline")} title={t("home.impressionsTitle")} subtitle={t("home.impressionsSub")} />
          </div>
          <SectionOrnament variant="divider" className="mb-16" />

          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-16">
            {GALLERY_SEED.slice(0, 6).map((img, i) => (
              <Reveal key={i} delay={i * 80} className={`zoom-parent ${i % 5 === 0 ? "row-span-2 aspect-[3/4]" : "aspect-square"}`}>
                <img src={img.url} alt={lang === "fr" ? img.alt_fr : img.alt_de} loading="lazy" className="w-full h-full object-cover zoom-img" />
              </Reveal>
            ))}
          </div>

          <div className="text-center">
            <Link to="/eindruecke" className="inline-flex items-center gap-2 text-[11px] tracking-[0.3em] uppercase border-b border-ink pb-1 hover:text-gold hover:border-gold transition-colors">
              {t("nav.impressions")} <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      {/* Reviews (Bewertungen) */}
      {reviews.length > 0 && (
        <section className="relative bg-secondary/40 py-24 sm:py-28 px-6 lg:px-10 overflow-hidden">
          <SectionOrnament variant="dots" />
          <div className="relative max-w-[1400px] mx-auto">
            <div className="mb-10">
              <SectionHeading overline={t("home.reviewsOverline")} title={t("home.reviewsTitle")} />
            </div>
            <SectionOrnament variant="divider" className="mb-16" />
            <div className="grid md:grid-cols-3 gap-8">
              {reviews.map((r) => (
                <Reveal key={r.id} className="bg-card border border-border p-8">
                  <StarRatingDisplay value={r.rating} />
                  <p className="text-ink/80 font-light italic leading-relaxed my-5">« {r.text} »</p>
                  <p className="text-sm tracking-wider uppercase text-ink-muted">{r.name} · {r.event_type}</p>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Instagram */}
      <section className="bg-ink py-24 sm:py-32 px-6 lg:px-10 overflow-hidden" data-testid="home-instagram-section">
        <div className="max-w-[1400px] mx-auto">
          <div className="grid lg:grid-cols-[1fr_1.3fr] gap-14 lg:gap-20 items-center">
            <Reveal>
              <p className="text-xs tracking-[0.35em] uppercase text-gold mb-6">{t("home.instagramOverline")}</p>
              <h2 className="font-heading font-light text-3xl sm:text-4xl lg:text-5xl leading-snug text-cream mb-6">
                {t("home.instagramTitle")}
              </h2>
              <p className="text-cream/70 font-light leading-relaxed mb-8 max-w-md">
                {t("home.instagramSub")}
              </p>
              <a
                href={igLink}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="home-instagram-cta"
                className="inline-flex items-center gap-3 border border-gold text-gold px-8 py-4 text-[11px] tracking-[0.3em] uppercase hover:bg-gold hover:text-ink transition-colors duration-500"
              >
                <Instagram size={16} strokeWidth={1.5} />
                {igHandle}
              </a>
            </Reveal>

            <div className="grid grid-cols-3 gap-2 sm:gap-3">
              {GALLERY_SEED.slice(6, 12).map((img, i) => (
                <Reveal key={i} delay={i * 70}>
                  <a
                    href={igLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block relative aspect-square overflow-hidden group"
                    data-testid={`home-instagram-tile-${i}`}
                  >
                    <img
                      src={img.url}
                      alt={lang === "fr" ? img.alt_fr : img.alt_de}
                      loading="lazy"
                      className="w-full h-full object-cover transition-transform duration-[900ms] ease-out group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-ink/0 group-hover:bg-ink/50 transition-colors duration-500 flex items-center justify-center">
                      <Instagram
                        size={26}
                        strokeWidth={1.4}
                        className="text-cream opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                      />
                    </div>
                  </a>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="relative py-32 px-6 overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url('${CTA_IMAGE}')` }} />
        <div className="absolute inset-0 bg-ink/60" />
        <div className="relative z-10 max-w-3xl mx-auto text-center text-cream">
          <Reveal>
            <h2 className="font-heading font-light text-4xl sm:text-5xl leading-tight mb-6">{t("home.finalTitle")}</h2>
            <p className="text-base sm:text-lg text-cream/85 font-light mb-10">{t("home.finalSub")}</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/termin" data-testid="final-cta-consultation" className="bg-gold text-ink px-9 py-4 text-[11px] tracking-[0.3em] uppercase hover:bg-cream transition-colors duration-500">
                {t("nav.cta")}
              </Link>
              <Link to="/anfrage" data-testid="final-cta-inquiry" className="border border-cream text-cream px-9 py-4 text-[11px] tracking-[0.3em] uppercase hover:bg-cream hover:text-ink transition-colors duration-500">
                {t("nav.inquiry")}
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
